import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  isNumberGreaterEqualMin,
  isNumberLessEqualMax,
  isValidEmail,
  isValidJson,
  isValidUrl
} from '@mng/mng-ts-helpers';
import { isLastDayOfMonth } from 'date-fns';

export type ValidatorFunc = (control: FormControl) => { [key: string]: any };

export const validateUrl = (control: FormControl<string>): ValidationErrors | null => {
  const valid = !control.value || isValidUrl(control.value);
  return !valid ? { url: true } : null;
};

export const validateEmail = (control: FormControl<string>): ValidationErrors | null => {
  const valid = !control.value || isValidEmail(control.value);
  return !valid ? { email: true } : null;
};

export const trueValidator = (control: FormControl<boolean>): ValidationErrors | null => {
  return !control.value ? { wrongBooleanResult: true } : null;
};

export const validateMin = (min: number): ValidatorFn => {
  return (control: FormControl<number>): ValidationErrors | null => {
    const valid = (!control.value && control.value !== 0) || isNumberGreaterEqualMin(control.value, min);
    return !valid ? { min: true } : null;
  };
};

export const validatorRegex = (regEx: RegExp, message?: string, required = true): ValidatorFn => {
  return (control: FormControl<number>): ValidationErrors | null => {
    if (!required && !control.value) {
      return null;
    }
    const value = control.value ? `${control.value}` : '';

    const valid = regEx.test(value);
    if (valid) {
      return null;
    } else {
      return message ? { [message]: true } : { pattern: true };
    }
  };
};

export const validateEven = (withFamilienbonus: boolean): ValidatorFn => {
  return (control: FormControl): ValidationErrors | null => {
    if (!withFamilienbonus) {
      return null;
    }
    const valid = !control.value || control.value % 2 === 0;
    return !valid ? { even: true } : null;
  };
};

export const validateMax = (max: number): ValidatorFn => {
  return (control: FormControl<number>): ValidationErrors | null => {
    const valid = (!control.value && control.value !== 0) || isNumberLessEqualMax(control.value, max);
    return !valid ? { max: true } : null;
  };
};

/**
 * @deprecated verwende stattdessen: createMatchValidator
 */
export const validateMatchingField = (controlGroupGetter: () => FormGroup, matchingField: string) => {
  return (control: FormControl): { [key: string]: any } => {
    // console.log('validateMatchingField1 with field ', matchingField, control.value);

    let valid = true;
    const group: FormGroup = controlGroupGetter();
    if (group) {
      const matchingFieldControl = group.controls[matchingField];

      // Don't kick in until user touches both fields
      if (matchingFieldControl.pristine || control.pristine) {
        return null;
      }

      valid = control.value === matchingFieldControl.value;
      const currentErrors = matchingFieldControl.errors;

      // we have to take care of existing errors in matchingFieldControl
      if (!valid) {
        if (currentErrors && currentErrors['machingField']) {
          // noting to do - already set
        } else if (currentErrors && Object.keys(currentErrors).length > 0) {
          matchingFieldControl.setErrors(Object.assign({}, currentErrors, { matchingField: true }));
        } else {
          matchingFieldControl.setErrors({ matchingField: true });
        }
      } else {
        if (currentErrors && Object.keys(currentErrors).length > 0) {
          delete currentErrors['matchingField'];
          if (Object.keys(currentErrors).length === 0) {
            matchingFieldControl.setErrors(null, { emitEvent: true });
          } else {
            matchingFieldControl.setErrors(currentErrors, { emitEvent: true });
          }
        } else {
          matchingFieldControl.setErrors(null, { emitEvent: true });
        }
      }
    }
    return !valid ? { matchingField: true } : null;
  };
};

export const validateAsync = (control: FormControl): Promise<ValidationErrors | null> => {
  return new Promise((resolve) => {
    // Async
    setTimeout(() => {
      // +--- Simulates Web API
      // |
      // v
      if (control.value === 'Graz' || control.value === 'Hamburg') {
        resolve(null);
      } else {
        // error case
        resolve({ ortAsync: true });
      }
    }, 3000);
  });
};

export const numbersValidator = (control: FormControl<string>): ValidationErrors | null => {
  const regex = /^[0-9]*$/;
  const valid = !control.value || regex.test(control.value);
  return valid ? null : { 'Nur Ziffern sind erlaubt': true };
};

export function requiredWhenValidator(whenRequired: (control: AbstractControl) => boolean) {
  return (control: AbstractControl): ValidationErrors | null => {
    const needsRequiredCheck = whenRequired(control);

    if (needsRequiredCheck && !control.value) {
      return { required: 'true' };
    }
    return null;
  };
}

export const validateMonatsletzter = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const ende = new Date(control.value);
    const valid = (!control.value && control.value !== null) || isLastDayOfMonth(ende);
    return !valid ? { 'Ablaufdatum muss ein Monatsletzter sein': true } : null;
  };
};

export const jsonValidator = (control: AbstractControl): ValidationErrors | null =>
  control.value && !isValidJson(control.value) ? { 'Ungültige JSON Daten': true } : null;
